<template>
  <div class="login-page">
    <div class="login-card">
      <h1>授权登录</h1>

      <div class="login-content">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>{{ statusMessage }}</p>
        </div>

        <!-- QR Code Display -->
        <div v-else-if="qrCodeBase64 && !error && !authorized">
          <p class="instruction-text">{{ statusMessage }}</p>
          <div class="qr-code-wrapper">
            <img :src="qrCodeBase64" alt="QR Code" />
          </div>
        </div>

        <!-- Error State -->
        <div v-if="error" class="error-state">
           <p>{{ error }}</p>
           <button @click="startRequest" class="retry-button">重试</button>
        </div>

        <!-- Success State -->
        <div v-if="authorized" class="success-state">
          <div class="success-icon">✓</div>
          <h2>授权成功</h2>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getTdcQrCode, getTdcStatus } from '@/api';
import  {
  executeIpv6Flow,
} from '../components/Ipv6Address'

export default {
  name: 'ExternalAuthPage',
  data() {
    return {
      authorized: false,
      loading: true,
      qrCodeBase64: '',
      statusMessage: '正在获取二维码...',
      error: null,
      pollingInterval: null,
      requestId: null,
      userDid: '', // To store the user's DID
    };
  },
  methods: {
    async startRequest() {
      this.loading = true;
      this.error = null;
      this.qrCodeBase64 = '';
      this.statusMessage = '正在获取二维码...';

      try {
        const response = await getTdcQrCode();
        if (response.data.code === 200) {
          this.qrCodeBase64 = 'data:image/png;base64,' + response.data.data.urlQrBase64;
          this.requestId = response.data.data.requestId;
          this.statusMessage = '请使用App扫描二维码授权';
          this.loading = false;
          this.startPolling();
        } else {
          throw new Error(response.data.message || '二维码接口返回数据格式不正确');
        }
      } catch (err) {
        this.loading = false;
        this.error = err.message || '获取二维码失败，请稍后重试。';
        console.error(err);
      }
    },
    startPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }

      this.pollingInterval = setInterval(async () => {
        if (!this.requestId) return;

        try {
          const response = await getTdcStatus(this.requestId);

          // Check if the outer response is successful and data is a string
          if (response.data && response.data.code === 200 && typeof response.data.data === 'string') {
            const innerData = JSON.parse(response.data.data);
            const status = innerData.code;

            switch (status) {
              case 200000:
                this.userDid = innerData.did; // Store the DID
                this.handleAuthorizationSuccess();
                break;
              case 400002:
                 this.error = `未获得对应凭证`;
                 this.stopPolling();
                 break;
              default:
                // Continue polling for other non-final states
                break;
            }
          } else if (response.data && response.data.code !== 200) {
            // Handle outer API error
            throw new Error(response.data.message || '获取状态失败');
          }
          // If data is not a string or parsing fails, the catch block will handle it
        } catch (err) {
          this.error = '获取状态失败，请重新尝试。';
          console.error('Polling error:', err);
          this.stopPolling();
        }
      }, 2000);
    },
    handleAuthorizationSuccess() {
      this.stopPolling();
      this.authorized = true;
      this.qrCodeBase64 = null;
      this.error = null;
      // @linhuajian 20260206 新增
      let taskUrlIp = "" // 从URL提取的UrlIP, url格式 http://192.168.1.1:8080/?ip=[2026:db8::1]
      if (window.location.search) {
        const urlParams = new URLSearchParams(window.location.search);
        taskUrlIp = urlParams.get('ip');
      }

      if (executeIpv6Flow(this.userDid, taskUrlIp)) {
        console.log('配置成功！');
      } else {
        console.error('配置失败！');
      }

      // 这里可以添加跳转逻辑，例如：
      // setTimeout(() => this.$router.push('/dashboard'), 2000);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
  },
  created() {
    this.startRequest();
  },
  beforeDestroy() {
    this.stopPolling();
  }
};
</script>

<style scoped>
.login-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.login-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  text-align: center;
}

h1 {
  color: #333;
  margin-bottom: 30px;
  font-size: 1.8em;
  font-weight: 600;
}

.login-content {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.instruction-text {
  font-size: 1.1em;
  color: #666;
  margin-bottom: 20px;
}

.qr-code-wrapper {
  padding: 10px;
  border: 1px solid #eee;
  border-radius: 4px;
  display: inline-block;
}

.qr-code-wrapper img {
  display: block;
  width: 200px;
  height: 200px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1890ff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

.error-state {
  color: #ff4d4f;
}

.retry-button {
  margin-top: 15px;
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 20px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.retry-button:hover {
  background-color: #40a9ff;
}

.success-icon {
  font-size: 4em;
  color: #52c41a;
  margin-bottom: 20px;
}

.success-state h2 {
  color: #333;
  margin-bottom: 10px;
}

.success-state p {
  color: #888;
  word-break: break-all; /* To wrap long DID strings */
}
</style>
